'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/** Minimum width of the panel in pixels. */
export const PANEL_MIN_WIDTH = 384;
/** Minimum width reserved for the main content area when the panel is open. */
export const MAIN_CONTENT_MIN_WIDTH = 384;
/**
 * Minimum container width required for side-by-side layout.
 * Below this threshold the panel takes over the full view (overlay mode).
 */
export const SIDE_BY_SIDE_MIN_WIDTH = PANEL_MIN_WIDTH + MAIN_CONTENT_MIN_WIDTH;
/** Pixel step used by keyboard resize (ArrowLeft / ArrowRight). */
export const PANEL_RESIZE_STEP = 10;
/**
 * Share of the PageContainer width the panel takes when it first opens.
 * A ratio rather than pixels so the split feels the same on a laptop and a wide monitor.
 */
export const PANEL_DEFAULT_WIDTH_RATIO = 3 / 4;

/**
 * Panel width before the user resizes it: `PANEL_DEFAULT_WIDTH_RATIO` of the container,
 * clamped so the panel keeps `PANEL_MIN_WIDTH` and the main content keeps `MAIN_CONTENT_MIN_WIDTH`.
 * Returns 0 below `SIDE_BY_SIDE_MIN_WIDTH`, where CSS `w-[100cqw]` takes over as an overlay.
 */
export function getDefaultPanelWidth(containerWidth: number): number {
  if (containerWidth < SIDE_BY_SIDE_MIN_WIDTH) return 0;
  const maxWidth = containerWidth - MAIN_CONTENT_MIN_WIDTH;
  return Math.min(maxWidth, Math.max(PANEL_MIN_WIDTH, containerWidth * PANEL_DEFAULT_WIDTH_RATIO));
}

/**
 * Returns the width of the nearest PageContainer ancestor.
 * Falls back to window.innerWidth if the container cannot be found.
 *
 * Using the container width (rather than window.innerWidth) means the sidebar
 * and any other layout outside PageContainer's scope are excluded from the
 * available resize range.
 */
export function getPanelContainerWidth(panelRef: React.RefObject<HTMLDivElement | null>): number {
  return (
    panelRef.current?.closest<HTMLElement>('[data-spellbook-name="page-container"]')?.getBoundingClientRect().width ?? window.innerWidth
  );
}

export type UsePanelResizeResult = {
  panelWidth: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  /** Keyboard resize: ArrowLeft widens, ArrowRight narrows; Home/End jump to min/max. */
  handleResizeKeyDown: (e: React.KeyboardEvent) => void;
};

/**
 * Manages panel resize state and event handlers.
 *
 * Handles mouse-drag resizing, keyboard-driven resizing, window-resize reset,
 * and panel-open-state reset. Does not manage focus or Escape-key behaviour —
 * those remain in the calling component so they stay co-located with the
 * elements they affect.
 */
export function usePanelResize(
  panelRef: React.RefObject<HTMLDivElement | null>,
  resizable: boolean,
  isOpen: boolean,
): UsePanelResizeResult {
  const [panelWidth, setPanelWidth] = useState<number>(0);
  const [isResizing, setIsResizing] = useState(false);
  // Always start as false so the initialization logic fires on first mount
  // regardless of whether the panel is open at mount time.
  const wasOpenRef = useRef(false);
  // Until the user drags or keyboard-resizes, the panel re-fits to the default share as the
  // container changes, e.g. when the app sidebar collapses right after the panel opens.
  const isWidthPinnedRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!resizable || !isOpen) return;
      e.preventDefault();
      isWidthPinnedRef.current = true;
      setIsResizing(true);
    },
    [resizable, isOpen],
  );

  // Keyboard resize: ArrowLeft widens, ArrowRight narrows; Home/End jump to min/max.
  // In RTL the panel is on the left, so arrow semantics invert.
  // maxWidth = PageContainer width minus the minimum reserved for the main content area.
  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!resizable || !isOpen) return;
      const isRtl = panelRef.current ? getComputedStyle(panelRef.current).direction === 'rtl' : false;
      const maxWidth = getPanelContainerWidth(panelRef) - MAIN_CONTENT_MIN_WIDTH;
      let nextWidth: number | null = null;
      const widenKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
      const narrowKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
      if (e.key === widenKey) {
        e.preventDefault();
        nextWidth = Math.min(maxWidth, (panelWidth || panelRef.current?.offsetWidth || 400) + PANEL_RESIZE_STEP);
      } else if (e.key === narrowKey) {
        e.preventDefault();
        nextWidth = Math.max(PANEL_MIN_WIDTH, (panelWidth || panelRef.current?.offsetWidth || 400) - PANEL_RESIZE_STEP);
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextWidth = PANEL_MIN_WIDTH;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextWidth = maxWidth;
      }
      if (nextWidth !== null) {
        isWidthPinnedRef.current = true;
        requestAnimationFrame(() => setPanelWidth(nextWidth));
      }
    },
    [resizable, isOpen, panelWidth, panelRef],
  );

  // Initialize panel width synchronously before the first paint so the CSS transition
  // only ever animates transform — never width. Without useLayoutEffect, the panel would
  // render with the CSS w-[100cqw] class first (starting a width transition), then the
  // async useEffect would set the pixel width and interrupt it mid-flight.
  // In side-by-side mode (container ≥ SIDE_BY_SIDE_MIN_WIDTH), lock in a stable pixel
  // width so JS — not fluid CSS cqw — controls the size.
  // In overlay mode (container too narrow), leave panelWidth at 0 so CSS w-[100cqw] fills the full view.
  useLayoutEffect(() => {
    if (!resizable || !isOpen) {
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      isWidthPinnedRef.current = false;
      setPanelWidth(getDefaultPanelWidth(getPanelContainerWidth(panelRef)));
    }

    wasOpenRef.current = isOpen;
  }, [resizable, isOpen, panelRef]);

  // Re-fit (default width) or clamp (pinned width) the panel on container resize. Runs as a
  // regular useEffect since ResizeObserver and window resize events fire outside React's render cycle.
  // Use both ResizeObserver (precise sub-container tracking) and window 'resize'
  // (fires every frame during live drag on all platforms — ResizeObserver defers
  // its callbacks until drag end on Chrome/Windows).
  useEffect(() => {
    if (!resizable || !isOpen) return;

    // Both callbacks run ahead of paint; a scheduled update would land a frame late, so the
    // panel and everything centered in it would visibly lag each resize step.
    const handleResize = () => {
      flushSync(() => {
        setPanelWidth((current) => {
          if (!isWidthPinnedRef.current) return getDefaultPanelWidth(getPanelContainerWidth(panelRef));
          if (current === 0) return 0; // CSS (cqw) is in control — don't interfere
          const maxWidth = getPanelContainerWidth(panelRef) - MAIN_CONTENT_MIN_WIDTH;
          // Not enough room for both panes at their minimum widths — let CSS go full-width overlay.
          if (maxWidth < PANEL_MIN_WIDTH) return 0;
          return Math.min(current, maxWidth);
        });
      });
    };

    const container = panelRef.current?.closest<HTMLElement>('[data-spellbook-name="page-container"]');
    const observer = new ResizeObserver(handleResize);
    if (container) {
      observer.observe(container);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [resizable, isOpen, panelRef]);

  // Mouse-drag resize: throttle via requestAnimationFrame and clamp between min/max.
  // Uses the panel's own right edge as the reference so the calculation stays correct
  // regardless of what's to the left of the PageContainer.
  useEffect(() => {
    if (!isResizing || !resizable || !isOpen) return;

    let animationFrameId: number | null = null;

    const isRtl = panelRef.current ? getComputedStyle(panelRef.current).direction === 'rtl' : false;

    const handleMouseMove = (e: MouseEvent) => {
      if (animationFrameId !== null) return;

      animationFrameId = requestAnimationFrame(() => {
        const rect = panelRef.current?.getBoundingClientRect();
        if (rect) {
          const maxWidth = getPanelContainerWidth(panelRef) - MAIN_CONTENT_MIN_WIDTH;
          // LTR: handle is on the left edge — width = distance from cursor to right edge.
          // RTL: handle is on the right edge — width = distance from left edge to cursor.
          const newWidth = isRtl ? e.clientX - rect.left : rect.right - e.clientX;
          setPanelWidth(Math.min(maxWidth, Math.max(PANEL_MIN_WIDTH, newWidth)));
        }
        animationFrameId = null;
      });
    };

    const handleMouseUp = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizable, isOpen, panelRef]);

  return { panelWidth, isResizing, handleMouseDown, handleResizeKeyDown };
}
